import { Signal, computed } from "@angular/core";
import type {
    FilterDefinition,
    FilterSingleSelection,
    FilterOption,
    FilterSelectionList,
    SupportedFilterValue,
    FilterValue,
    Predicate,
} from "@filter/filter.types";
import type { TranslateObject } from "@app/views-interfaces";
import { FilterSelectionKind, createDefaultSelection } from "@filter/filter.types";
import { SimpleFilter } from "./simple-filter";
import { compareString } from "@shared/utils";

export abstract class SimpleFilterSingle<TItem, TSelection extends FilterSelectionList = FilterSingleSelection> extends SimpleFilter<
    TSelection,
    TItem
> {
    protected abstract getValue(item: TItem): FilterValue | undefined;
    protected abstract getLabel(value: FilterValue): string | undefined | null | TranslateObject;
    protected abstract getAllValues(): readonly FilterValue[];
    protected noneOption: FilterOption | null = null;

    public readonly options = computed<readonly FilterOption[]>(() => this.getOptions(this.getAllValues()));

    public readonly buildOptions = (data: Signal<TItem[]>): Signal<readonly FilterOption[] | undefined> =>
        computed(() => {
            const items = data();
            if (!items?.length) return;

            return this.getOptions(items.map((item) => this.getValue(item)));
        });

    private getOptions(values: readonly (FilterValue | undefined)[]): FilterOption[] {
        const options = new Map<FilterValue, FilterOption>();
        for (const value of values) {
            if (value === undefined || value === null || options.has(value)) continue;

            const label = this.getLabel(value);
            if (label) options.set(value, { value, label });
        }

        const sortedOptions = Array.from(options.values()).sort((a, b) => compareString(a.label as string, b.label as string));
        if (this.noneOption) sortedOptions.unshift(this.noneOption);

        return sortedOptions;
    }

    protected createDefinition(): FilterDefinition<TSelection> {
        return {
            ...super.createDefinition(),
            kind: FilterSelectionKind.Single,
            defaultSelection: createDefaultSelection(FilterSelectionKind.Single) as TSelection,
        };
    }

    protected normalize(value: SupportedFilterValue): TSelection {
        return {
            kind: FilterSelectionKind.Single,
            value: value,
        } as TSelection;
    }

    protected getPredicate(selection: TSelection): Predicate<TItem> {
        const singleSelection = selection as FilterSingleSelection;
        return (item) => {
            const value = this.getValue(item);
            if (value !== undefined && singleSelection.value === value) return true;
            if (!this.noneOption || singleSelection.value !== this.noneOption.value) return false;

            const finalValue = !value ? value : this.getLabel(value);
            return finalValue === undefined && singleSelection.value === this.noneOption.value;
        };
    }
}
